import React, { useState, useEffect, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Flex,
  Text,
  Box,
  Textarea,
  Input,
} from '@chakra-ui/core'
import { Button } from 'components/ui'
import { Star, AlertTriangle, XCircle } from 'lucide-react'
import keys from 'constants/keys'
import useLocaleText from 'components/useLocaleText'
import { Reservations } from 'constants/interfaces'
import { ReasonsList, ReasonItem } from 'components/TasksV2/UI/ReasonComponents'
import { ReasonConfig } from 'components/TasksV2/UI/ReasonComponents/reasonsConfig'

export type ReservationMode = 'reject' | 'approve_with_reservation'

interface ReservationsModalProps {
  isSaving?: boolean
  error?: string
  isOpen: boolean
  onClose: () => void
  onConfirm: (reservations: Reservations, rating: number) => void
  mode: ReservationMode
  initialRating?: number
  initialReservations?: Reservations
  predefinedReasons?: ReasonConfig[]
}

export const ReservationsModal: React.FC<ReservationsModalProps> = ({
  isSaving = false,
  error,
  isOpen,
  onClose,
  onConfirm,
  mode,
  initialRating = 0,
  initialReservations,
  predefinedReasons = [],
}) => {
  const [rating, setRating] = useState(initialRating)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [reasons, setReasons] = useState<ReasonItem[]>([])
  const [customReason, setCustomReason] = useState('')
  const [customScore, setCustomScore] = useState('')

  // Translations
  const t_reject_title = useLocaleText('reject_with_reasons')
  const t_approve_reservation_title = useLocaleText('approve_with_reservation')
  const t_select_reasons = useLocaleText('t_select_one_or_more')
  const t_total_deduction = useLocaleText('total_deduction')
  const t_cancel = useLocaleText('cancel')
  const t_confirm = useLocaleText('save')
  const t_rate_submission = useLocaleText('add_rating')
  const t_enter_reason = useLocaleText('add_reason')
  const t_custom_score = useLocaleText('custom_score')

  const isRejectMode = mode === 'reject'
  const hasPredefinedReasons = predefinedReasons.length > 0

  // Use predefinedReasons directly (empty array for non-live retailers)
  const reasonsConfig = useMemo(() => predefinedReasons, [predefinedReasons])
  const predefinedKeys = useMemo(() => reasonsConfig.map((r) => r.key), [reasonsConfig])

  const toggleReason = (key: string) => {
    setReasons((prev) => {
      const newReasons = prev.map((r) => (r.key === key ? { ...r, isSelected: !r.isSelected } : r))

      // For approve with reservation mode, check if total exceeds 100%
      if (!isRejectMode) {
        const total = newReasons.filter((r) => r.isSelected).reduce((acc, r) => acc + (r.score || 0), 0)
        if (total > 100) {
          return prev // Don't update if exceeds 100%
        }
      }

      return newReasons
    })
  }

  const totalDeduction = useMemo(() => {
    if (hasPredefinedReasons) {
      const sum = reasons.filter((r) => r.isSelected).reduce((acc, r) => acc + (r.score || 0), 0)
      return Math.min(100, sum)
    } else {
      // For custom reason, use customScore
      return isRejectMode ? 100 : Math.min(100, Math.max(0, parseInt(customScore) || 0))
    }
  }, [reasons, customScore, isRejectMode, hasPredefinedReasons])

  const handleConfirm = () => {
    let reasonsArray: string[] = []
    let scoreDeduction = 0

    if (hasPredefinedReasons) {
      // Use selected predefined reasons
      reasonsArray = reasons.filter((r) => r.isSelected).map((r) => r.key)
      scoreDeduction = isRejectMode ? 100 : totalDeduction
    } else {
      // Use custom reason from textarea
      if (customReason.trim()) {
        reasonsArray = [customReason.trim()]
        scoreDeduction = isRejectMode ? 100 : Math.min(100, Math.max(0, parseInt(customScore) || 0))
      }
    }

    const reservations: Reservations = {
      reasons: reasonsArray,
      score_deduction: scoreDeduction,
    }
    onConfirm(reservations, rating)
  }

  const isValid = hasPredefinedReasons ? reasons.some((r) => r.isSelected) : customReason.trim().length > 0

  const displayRating = hoveredRating > 0 ? hoveredRating : rating

  // Get rating label translation (must be called unconditionally)
  const ratingLabelKey = rating > 0 ? keys.RATING_LABELS[rating] : ''
  const ratingLabel = useLocaleText(ratingLabelKey || 'poor', true)

  // Initialize reasons from config
  useEffect(() => {
    if (!isOpen) return

    if (hasPredefinedReasons) {
      const configReasons = reasonsConfig.map((reason) => ({
        key: reason.key,
        score: reason.score,
        isSelected: initialReservations?.reasons.includes(reason.key) ?? false,
      }))

      // Add any custom reasons from initialReservations
      if (initialReservations) {
        const customReasons = initialReservations.reasons
          .filter((r) => !predefinedKeys.includes(r))
          .map((r) => ({
            key: r,
            score: 0, // Custom reasons from initial don't have stored scores
            isSelected: true,
          }))
        setReasons([...configReasons, ...customReasons])
      } else {
        setReasons(configReasons)
      }
    }

    setRating(initialRating)
    setHoveredRating(0)
    setCustomReason('')
    setCustomScore('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSaving ? () => undefined : onClose}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={!isSaving}
      size="lg"
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.5)" zIndex={1500} />
      <ModalContent maxW="550px" borderRadius="12px" overflow="hidden" maxH="90vh" zIndex={1501}>
        <ModalHeader
          py="16px"
          px="24px"
          bg={isRejectMode ? '#FEE2E2' : '#FEF3C7'}
          borderBottom="1px solid"
          borderColor={isRejectMode ? '#FCA5A5' : '#FCD34D'}
        >
          <Flex align="center" style={{ gap: '12px' }}>
            {isRejectMode ? <XCircle size={24} color="#DC2626" /> : <AlertTriangle size={24} color="#D97706" />}
            <Text fontSize="18px" fontWeight="600" color={isRejectMode ? '#DC2626' : '#D97706'}>
              {isRejectMode ? t_reject_title : t_approve_reservation_title}
            </Text>
          </Flex>
        </ModalHeader>

        <ModalBody py="24px" px="24px" bg="#FFFFFF" overflowY="auto">
          <Flex direction="column" style={{ gap: '24px' }}>
            {/* Rating Section - only for approve_with_reservation mode */}
            {!isRejectMode && (
              <Box>
                <Text fontSize="14px" fontWeight="600" color="#374151" mb="12px">
                  {t_rate_submission}
                </Text>
                <Flex style={{ gap: '8px' }} align="center" justify="center">
                  {[1, 2, 3, 4, 5].map((starValue) => (
                    <Button
                      type="button"
                      variant="icon"
                      aria-label={`${t_rate_submission}: ${starValue} / 5`}
                      aria-pressed={rating === starValue}
                      isDisabled={isSaving}
                      key={starValue}
                      onClick={() => setRating(starValue)}
                      onMouseEnter={() => setHoveredRating(starValue)}
                      onMouseLeave={() => setHoveredRating(0)}
                      cursor="pointer"
                      transition="all 0.2s"
                      transform={displayRating >= starValue ? 'scale(1.1)' : 'scale(1)'}
                      _hover={{ transform: 'scale(1.15)' }}
                      _active={{ transform: 'scale(1.05)' }}
                      p="4px"
                    >
                      <Star
                        size={32}
                        fill={displayRating >= starValue ? '#FBBF24' : 'none'}
                        stroke={displayRating >= starValue ? '#FBBF24' : '#D1D5DB'}
                        strokeWidth={displayRating >= starValue ? 0 : 2}
                      />
                    </Button>
                  ))}
                </Flex>
                {rating > 0 && (
                  <Text fontSize="12px" color="#6B7280" fontWeight="500" textAlign="center" mt="8px">
                    {ratingLabel}
                  </Text>
                )}
              </Box>
            )}

            {hasPredefinedReasons ? (
              /* Show predefined reasons list */
              <>
                <ReasonsList
                  title={t_select_reasons}
                  reasons={reasons}
                  onToggle={toggleReason}
                  variant={isRejectMode ? 'reject' : 'warning'}
                  showScore={!isRejectMode}
                />

                {/* Total Deduction Display */}
                {!isRejectMode && (
                  <Box p="12px" bg="#FEE2E2" borderRadius="8px" border="1px solid" borderColor="#FCA5A5">
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" fontWeight="600" color="#374151">
                        {t_total_deduction}
                      </Text>
                      <Text fontSize="18px" fontWeight="700" color="#DC2626">
                        -{totalDeduction}%
                      </Text>
                    </Flex>
                  </Box>
                )}
              </>
            ) : (
              /* Show simple textarea for custom reason */
              <>
                <Textarea
                  value={customReason}
                  onChange={(e: any) => setCustomReason(e.target.value)}
                  placeholder={t_enter_reason || t_select_reasons}
                  height="100px"
                  resize="none"
                  borderRadius="8px"
                  borderColor="#E5E7EB"
                  _focus={{
                    borderColor: isRejectMode ? '#EF4444' : '#F59E0B',
                    boxShadow: isRejectMode ? '0 0 0 1px #EF4444' : '0 0 0 1px #F59E0B',
                  }}
                  fontSize="14px"
                />

                {/* Score input for approve with reservation mode */}
                {!isRejectMode && (
                  <Flex direction="column" style={{ gap: '8px' }}>
                    <Text fontSize="14px" fontWeight="600" color="#374151">
                      {t_custom_score}
                    </Text>
                    <Input
                      value={customScore}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomScore(e.target.value)}
                      placeholder="0-100"
                      type="number"
                      min="0"
                      max="100"
                      borderRadius="8px"
                      borderColor="#E5E7EB"
                      _focus={{ borderColor: '#F59E0B', boxShadow: '0 0 0 1px #F59E0B' }}
                      fontSize="14px"
                    />
                    {/* Total Deduction Display */}
                    <Box p="12px" bg="#FEE2E2" borderRadius="8px" border="1px solid" borderColor="#FCA5A5">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="14px" fontWeight="600" color="#374151">
                          {t_total_deduction}
                        </Text>
                        <Text fontSize="18px" fontWeight="700" color="#DC2626">
                          -{totalDeduction}%
                        </Text>
                      </Flex>
                    </Box>
                  </Flex>
                )}
              </>
            )}
          </Flex>
        </ModalBody>

        {error && (
          <Text role="alert" color="red.500" px={6}>
            {error}
          </Text>
        )}
        <ModalFooter py="16px" px="24px" borderTop="1px solid #E5E7EB" bg="#F9FAFB" justifyContent="space-between">
          <Button
            isDisabled={isSaving}
            onClick={onClose}
            variant="ghost"
            color="#6B7280"
            fontSize="14px"
            fontWeight="500"
            _hover={{ bg: '#F3F4F6', color: '#374151' }}
            _focus={{ boxShadow: 'none' }}
          >
            {t_cancel}
          </Button>

          <Button
            isLoading={isSaving}
            onClick={handleConfirm}
            bg={isRejectMode ? '#DC2626' : '#D97706'}
            color="#FFFFFF"
            fontSize="14px"
            fontWeight="600"
            px="24px"
            py="10px"
            borderRadius="6px"
            isDisabled={!isValid}
            _hover={{
              bg: isRejectMode ? '#B91C1C' : '#B45309',
              transform: 'translateY(-1px)',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            _disabled={{
              bg: '#D1D5DB',
              cursor: 'not-allowed',
              opacity: 0.6,
            }}
            _focus={{ boxShadow: 'none' }}
            transition="all 0.2s"
          >
            {t_confirm}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
